import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LiliTooltip, LiliToolTipColor } from '@lili/lili-frontend-common';
import { useInvoicePaymentsQuery } from '../../../../hooks/query/useInvoicePaymentsQuery';
import { useStore } from '../../../../store';
import { PaymentItem } from './PaymentItem';
import Heading from '../../../../generic-components/Heading';
import { useSetSelectedPaymentOption } from '../../../../hooks/mutations/useSetSelectedPaymentOption';
import Icon from '../../../../generic-components/Icon';
import infoIcon from '../../../../assets/svg/info-icon.svg';
import { STYLES, TEXTS } from '../consts/consts';
import { hasOverflowYContent } from '../../../../helpers';

const {
  PAGES: {
    PAYMENTS: { PAYMENTS_LIST },
  },
} = STYLES;
const { STEP_PAYMENTS } = TEXTS;

interface IProps {
  isScrollable?: (mode: boolean) => void;
}

export const PaymentsList = ({ isScrollable }: IProps) => {
  const elScrollableWrapper = useRef<HTMLDivElement>(null);
  const {
    user: { customerId },
  } = useStore((state) => state);
  const { t } = useTranslation();
  const [activeExpandId, setActiveExpandId] = useState<string>('');
  const [selectedPaymentData, setSelectedPaymentData] = useState<{ type: string; selected: boolean }>({
    type: '',
    selected: false,
  });
  const {
    refetch: fetchInvoicePayments,
    data: paymentOptions,
    isError,
  } = useInvoicePaymentsQuery({
    customerId,
    onSuccess: () => {},
  });
  const { mutateAsync: mutatePaymentOption } = useSetSelectedPaymentOption({
    customerId,
    type: selectedPaymentData.type,
    selected: selectedPaymentData.selected,
  });
  const connectedPayments = useMemo(() => {
    if (!paymentOptions || !paymentOptions?.customerOptionsInfo?.length) return [];
    const list: {}[] = [];
    paymentOptions.paymentOptionsInfo.forEach((method: any) => {
      const idx = paymentOptions.customerOptionsInfo.findIndex((i: any) => i.type === method.type);
      if (idx !== -1) {
        list.push({ ...method, ...paymentOptions.customerOptionsInfo[idx] });
      }
    });
    return list;
  }, [paymentOptions]);
  const notConnectedPayments = useMemo(() => {
    if (!paymentOptions) return [];
    if (!connectedPayments.length) {
      return paymentOptions.paymentOptionsInfo;
    }
    const list: {}[] = [];
    paymentOptions.paymentOptionsInfo.forEach((method: any) => {
      const idx = paymentOptions.customerOptionsInfo.findIndex((i: any) => i.type === method.type);
      if (idx === -1) {
        list.push(method);
      }
    });
    return list;
  }, [connectedPayments]);

  const isConnectedPaymentMethods = useMemo(() => connectedPayments?.length > 0, [connectedPayments]);

  const isAllowToAddMorePayments = useMemo(() => {
    if (connectedPayments.filter((i: any) => i.selected).length >= 3) return false;
    return true;
  }, [connectedPayments]);

  const updateActiveExpandId = (id: string) => {
    if (id === activeExpandId) {
      setActiveExpandId('');
    } else {
      setActiveExpandId(id);
    }
  };

  const handleSetSelectedPayment = (type: string, selected: boolean) => {
    setSelectedPaymentData({ type, selected });
  };

  useEffect(() => {
    if (!selectedPaymentData.type) return;
    mutatePaymentOption().then(() => {
      fetchInvoicePayments();
    });
  }, [selectedPaymentData]);

  useEffect(() => {
    if (elScrollableWrapper.current) {
      if (isScrollable) {
        isScrollable(hasOverflowYContent(elScrollableWrapper.current));
      }
    }
  }, [elScrollableWrapper.current, elScrollableWrapper?.current?.scrollHeight]);

  return (
    <div className={PAYMENTS_LIST.CONTAINER}>
      <div
        ref={elScrollableWrapper}
        className={`flex flex-col mx-auto gap-[12px] w-[514px] ${isConnectedPaymentMethods ? 'py-[35px]' : 'py-[8px]'} inset-0 absolute`}
      >
        {isConnectedPaymentMethods && (
          <div className='flex items-center relative w-fit'>
            <Heading title={t(STEP_PAYMENTS.PAYMENT_LIST_CONNECTED)} className={PAYMENTS_LIST.PAYMENTS_GROUP_TITLE} />
            <span data-tooltip-id='connected-tooltip' data-tooltip-place='right'>
              <Icon src={infoIcon} className={PAYMENTS_LIST.PAYMENTS_GROUP_ICON} />
            </span>
            <LiliTooltip
              description={t('INVOICES.STEP_PAYMENTS.CONNECTED_PAYMENT_OPTIONS')}
              targetId='connected-tooltip'
              color={LiliToolTipColor.White}
              className='min-w-[340px]'
            />
          </div>
        )}
        <div className='flex flex-col gap-[12px] mb-[22px]'>
          {isConnectedPaymentMethods &&
            connectedPayments.map((method: any) => (
              <PaymentItem
                triggerReFetchOptionsList={fetchInvoicePayments}
                handleSetSelectedPayment={handleSetSelectedPayment}
                paymentData={method}
                key={method?.type}
                mutateExpandId={updateActiveExpandId}
                activeExpandId={activeExpandId}
                allowActive={isAllowToAddMorePayments}
              />
            ))}
        </div>

        {notConnectedPayments && notConnectedPayments.length > 0 && (
          <div className='flex items-center relative w-fit'>
            <Heading
              title={t(STEP_PAYMENTS.PAYMENT_LIST_NOT_CONNECTED)}
              className={PAYMENTS_LIST.PAYMENTS_GROUP_TITLE}
            />
            <span data-tooltip-id='additional-payment-options' data-tooltip-place='right'>
              <Icon src={infoIcon} className={PAYMENTS_LIST.PAYMENTS_GROUP_ICON} />
            </span>
            <LiliTooltip
              description={t('INVOICES.STEP_PAYMENTS.ADDITIONAL_PAYMENT_METHODS')}
              targetId='additional-payment-options'
              color={LiliToolTipColor.White}
            />
          </div>
        )}
        <div className='flex flex-col gap-[12px]'>
          {notConnectedPayments &&
            notConnectedPayments.length > 0 &&
            notConnectedPayments.map((method: any, idx: number) => (
              <PaymentItem
                triggerReFetchOptionsList={fetchInvoicePayments}
                handleSetSelectedPayment={() => {}}
                paymentData={method}
                key={method?.type}
                mutateExpandId={updateActiveExpandId}
                activeExpandId={activeExpandId}
                allowActive={isAllowToAddMorePayments}
                isLastItem={notConnectedPayments.length - 1 === idx}
              />
            ))}
        </div>

        {isError && <div>{t(STEP_PAYMENTS.PAYMENT_LIST_NOT_LOADED)}</div>}
      </div>
    </div>
  );
};
